import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';
import { listQuery } from './listKeys';
import type { PeDrilldown } from './types';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type DrilldownNotFoundReason = 'not_found' | 'no_membership';

export class DrilldownNotFound extends Error {
  readonly reason: DrilldownNotFoundReason;

  constructor(reason: DrilldownNotFoundReason) {
    super(`program-evaluation drill-down: ${reason}`);
    this.name = 'DrilldownNotFound';
    this.reason = reason;
  }
}

export class UnknownListKey extends Error {
  constructor(key: string) {
    super(`program-evaluation drill-down: unknown list ${key}`);
    this.name = 'UnknownListKey';
  }
}

export type StudentRow = {
  ref: string;
  studentKey: string;
  altKeys: string[];
  school: string;
  grade: number;
  gradeLabel: string;
  scale?: string;
  composite: number | null;
  compositeScale?: number | null;
  speaking: number | null;
  speakingScale?: number | null;
  yearsInProgram: number;
  exitStanding: string;
  cohorts: string[];
  standing: string;
};

export type StudentList = {
  key: string;
  schoolYear: string;
  generatedAt: string;
  version: number;
  count: number;
  offset?: number;
  nextOffset?: number | null;
  students: StudentRow[];
};

export type StudentSnapshot = {
  student_key: string;
  alt_keys: string[] | null;
  standing: string;
  standing_source?: string;
  school: string;
  grade: number;
  grade_label: string;
  years_in_program?: number;
  tenure_provenance?: string;
  scale?: string;
  composite?: number | null;
  composite_scale?: number | null;
  domains?: Record<string, number> | null;
  domain_scales?: Record<string, number> | null;
  prior_composite?: number | null;
  prior_composite_scale?: number | null;
  prior_domains?: Record<string, number> | null;
  prior_domain_scales?: Record<string, number> | null;
  prior_year_end?: number;
  start_bucket?: string;
  exit_standing?: string;
  exit_reason?: string;
  exit_year_end?: number | null;
  cohorts: string[] | null;
  program_model?: string;
  dually_identified?: boolean;
  flashlight?: Record<string, Record<string, number>> | null;
};

export type DrilldownResponse = {
  schoolYear: string;
  generatedAt: string;
  version: number;
  schoolsOmitted?: number;
  drilldown: PeDrilldown;
};

export type StudentSnapshotResponse = {
  schoolYear: string;
  generatedAt: string;
  version: number;
  listKey?: string;
  student: StudentSnapshot;
  countedIn: string[];
};

type Query = Record<string, string | number | undefined>;

async function get<T>(path: string, query: Query): Promise<T> {
  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(name, String(value));
  }
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}${path}?${params.toString()}`, { headers: authHeaders(token) });
  if (response.status === 404 || response.status === 400) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    if (response.status === 400 && body.error === 'unknown_list') throw new UnknownListKey(String(query.key ?? query.ref ?? ''));
    if (response.status === 404) throw new DrilldownNotFound(body.error === 'no_membership' ? 'no_membership' : 'not_found');
  }
  if (!response.ok) {
    throw new Error(`program-evaluation drill-down fetch failed: ${response.status}`);
  }
  return (await response.json()) as T;
}

export function fetchStudentList(
  key: string,
  opts: { schoolYear?: string; version?: number | null; offset?: number } = {},
): Promise<StudentList> {
  return get<StudentList>('/program-evaluation/students', {
    ...listQuery(key),
    schoolYear: opts.schoolYear,
    version: opts.version ?? undefined,
    offset: opts.offset,
  });
}

export function fetchStudentSnapshot(
  ref: string,
  opts: { key?: string; schoolYear?: string; version?: number | null } = {},
): Promise<StudentSnapshotResponse> {
  const list: { key?: string; ref?: string } = opts.key === undefined ? {} : listQuery(opts.key);
  return get<StudentSnapshotResponse>('/program-evaluation/student', {
    ref,
    key: list.key,
    schoolYear: opts.schoolYear,
    version: opts.version ?? undefined,
  });
}

export function fetchDrilldown(opts: { schoolYear?: string; version?: number | null } = {}): Promise<DrilldownResponse> {
  return get<DrilldownResponse>('/program-evaluation/drilldown', { schoolYear: opts.schoolYear, version: opts.version ?? undefined });
}
